import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { NavLink, Outlet, useSearchParams } from "react-router-dom";
import { api, type AuthMeResponse } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";

const ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: "Falha ao concluir o login com o Google. Tente novamente.",
  userinfo_failed:
    "Não foi possível obter seus dados do Google. Tente novamente em alguns instantes.",
  no_verified_email:
    "Sua conta Google não tem um e-mail verificado. Use uma conta verificada para entrar.",
  forbidden:
    "Esta conta Google não tem permissão de admin. Peça para o responsável incluir seu e-mail na lista de admins.",
  oauth_not_configured:
    "O login com Google ainda não foi configurado neste servidor.",
};

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const queryClient = useQueryClient();

  const meQuery = useQuery<AuthMeResponse>({
    queryKey: ["auth", "me"],
    queryFn: api.authMe,
    staleTime: 0,
    retry: false,
  });

  const logout = useMutation({
    mutationFn: api.authLogout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  const errorMessage = useMemo(
    () => (errorCode ? ERROR_MESSAGES[errorCode] ?? `Erro: ${errorCode}` : null),
    [errorCode],
  );

  const dismissError = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    setSearchParams(next, { replace: true });
  };

  if (meQuery.isLoading) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Loading label="Carregando admin..." />
      </div>
    );
  }
  if (meQuery.error) {
    return <ErrorBox error={meQuery.error} />;
  }

  const me = meQuery.data;
  const user = me?.user ?? null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto">
        <div className="rounded-2xl border border-line bg-white p-8 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Área restrita
          </div>
          <h1 className="font-display text-[32px] leading-tight font-bold tracking-tight text-ink mb-2">
            Admin Liga Legacy
          </h1>
          <p className="text-[15px] leading-relaxed text-[#6b6356] mb-6">
            Esta área é restrita aos organizadores da liga. Entre com sua conta
            Google autorizada para continuar.
          </p>

          {errorMessage && (
            <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-3">
              <span className="flex-1">{errorMessage}</span>
              <button
                type="button"
                onClick={dismissError}
                className="text-red-500 hover:text-red-700 font-bold"
                aria-label="Fechar aviso"
              >
                ×
              </button>
            </div>
          )}

          {me?.configured === false ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              O login com Google ainda não está configurado neste servidor.
              Defina <code>GOOGLE_CLIENT_ID</code> e{" "}
              <code>GOOGLE_CLIENT_SECRET</code> nas variáveis de ambiente.
            </div>
          ) : (
            <a
              href="/api/auth/google"
              className="inline-flex items-center justify-center gap-3 w-full bg-white border border-line hover:border-[#c4c0b6] hover:bg-[#fbfaf6] transition-colors rounded-xl px-5 py-3 font-semibold text-ink"
            >
              <GoogleMark />
              Continuar com Google
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Admin
          </div>
          <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
            Painel
          </h1>
        </div>

        <div className="flex items-center gap-3 bg-white border border-line rounded-xl py-1.5 pl-1.5 pr-3">
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-9 h-9 rounded-lg object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="w-9 h-9 rounded-lg bg-brand-700 text-white flex items-center justify-center font-display font-bold">
              {user.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="flex flex-col leading-tight pr-2">
            <span className="text-sm font-semibold text-ink">{user.name}</span>
            <span className="text-xs text-[#8a8170]">{user.email}</span>
          </div>
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="text-sm font-semibold text-[#6b6356] hover:text-ink transition-colors px-2"
          >
            Sair
          </button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1.5 bg-white border border-line rounded-xl p-1.5 w-fit">
        <NavLink to="/theadmin/import" className={subNavLinkClass}>
          Importar do Melee
        </NavLink>
        <NavLink to="/theadmin/leagues" className={subNavLinkClass}>
          Ligas
        </NavLink>
        <NavLink to="/theadmin/events" className={subNavLinkClass}>
          Eventos
        </NavLink>
        <NavLink to="/theadmin/archetypes" className={subNavLinkClass}>
          Arquétipos
        </NavLink>
        <NavLink to="/theadmin/decks" className={subNavLinkClass}>
          Decks
        </NavLink>
        <UnclassifiedNavLink />
      </nav>

      <Outlet />
    </div>
  );
}

/**
 * Side-nav entry for the backlog of unclassified event_results. We
 * prefetch the count here so the badge is always visible — admins can see
 * at a glance how much work is pending without having to click in.
 */
function UnclassifiedNavLink() {
  const { data } = useQuery({
    queryKey: ["admin", "unclassified"],
    queryFn: api.adminUnclassifiedEventResults,
    staleTime: 30_000,
  });
  const count = data?.length ?? 0;
  return (
    <NavLink to="/theadmin/unclassified" className={subNavLinkClass}>
      {({ isActive }) => (
        <span className="flex items-center gap-2">
          Sem classificação
          {count > 0 && (
            <span
              className={[
                "inline-flex items-center justify-center min-w-[1.5rem] px-1.5 rounded-full text-[11px] font-bold tabular-nums",
                isActive
                  ? "bg-white/20 text-white"
                  : "bg-amber-100 text-amber-900",
              ].join(" ")}
            >
              {count}
            </span>
          )}
        </span>
      )}
    </NavLink>
  );
}

const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
    isActive
      ? "bg-brand-700 text-white"
      : "text-[#6b6356] hover:bg-[#f3efe6] hover:text-ink",
  ].join(" ");

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.708A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.708V4.96H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.96l3.007 2.332C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
